import { Form, Formik } from 'formik';
import { FC, useEffect, useState } from 'react';

import { CheckoutDescription } from 'src/checkout/components/CheckoutDescription/CheckoutDescription';
import LicenseFinder from 'src/components/Checkout/LicenseFinder/LicenseFinder';
import licenseData from 'src/components/Checkout/LicenseFinder/LicenseFinder.mock';
import { MICROCOPY } from 'src/data/microcopy/microcopyDictionary';
import { Button } from 'src/general/components/Button/Button';
import { InputFormik } from 'src/general/components/forms/Input/InputFormik';
import { InputMaskedFormik } from 'src/general/components/forms/Input/InputMaskedFormik';
import { InputTimeFormik } from 'src/general/components/forms/Input/InputTimeFormik';
import { getMicrocopy } from 'src/stores/microcopyStore';
import { pushToDataLayer } from 'src/utils/pushToDataLayer';

import styles from './PersonalizedQuoteForm.module.scss';
import {
  PersonalDetailsFormValidation,
  PersonalDetailsFormValues,
  PersonalizedQuoteFormProps,
} from './PersonalizedQuoteForm.type';

const initialValues: PersonalDetailsFormValues = {
  firstName: '',
  lastName: '',
  middleName: undefined,
  dateOfBirth: '',
  abn: '',
  isBusinessUseSelected: false,
  phone: '',
  licenceNumber: '',
};

export const PersonalDetailsForm: FC<PersonalizedQuoteFormProps<PersonalDetailsFormValues>> = ({
  onSubmit,
  values,
  onBack,
}) => {
  const [isLicenseFinderOpen, setIsLicenseFinderOpen] = useState(false);

  const onOpenLicenseFinder = () => {
    setIsLicenseFinderOpen(true);
  };
  const onCloseLicenseFinder = () => {
    setIsLicenseFinderOpen(false);
  };

  useEffect(() => {
    if (isLicenseFinderOpen) {
      pushToDataLayer({
        event: `checkout_click`,
        value: `license_finder`,
      });
    }
  }, [isLicenseFinderOpen]);

  return (
    <>
      <CheckoutDescription
        title={getMicrocopy(
          values.isBusinessUseSelected
            ? MICROCOPY.PERSONALIZED_QUOTE_PERSONAL_DETAILS_FORM_BUSINESS_HEADER
            : MICROCOPY.PERSONALIZED_QUOTE_PERSONAL_DETAILS_FORM_PERSONAL_HEADER,
        )}
        onBack={onBack}
        description={
          'We’ll use these details to provide your personalised rates through a soft credit check. This won’t affect your credit score.'
        }
      />
      <div className={styles.loanDetails} data-testid="PersonalDetailsForm">
        <Formik
          initialValues={{ ...initialValues, ...values }}
          onSubmit={onSubmit}
          validateOnChange={true}
          validationSchema={PersonalDetailsFormValidation}
        >
          {(props) => (
            <Form className={styles.form}>
              {values.isBusinessUseSelected && (
                <InputMaskedFormik
                  id="abn"
                  name="abn"
                  label="ABN"
                  type="text"
                  inputMode="numeric"
                  mask="00 000 000 000"
                  data-testid="business-abn"
                />
              )}
              <InputFormik
                id="firstName"
                name="firstName"
                label="First name"
                type="text"
                data-testid="buyer-first-name"
              />
              <InputFormik id="middleName" name="middleName" label="Middle name" type="text" />
              <InputFormik id="lastName" name="lastName" label="Last name" type="text" data-testid="buyer-last-name" />
              <InputMaskedFormik
                id="phone"
                name="phone"
                label="Mobile number"
                note="e.g. 04XX XXX XXX"
                mask="0000 000 000"
                required
                data-testid="personalised-quote-form-mobile-number"
              />
              <InputTimeFormik
                name="dateOfBirth"
                id="dateOfBirth"
                label="Date of birth"
                data-testid="buyer-date-of-birth"
                variant="date"
              />
              <InputFormik
                id="licenceNumber"
                name="licenceNumber"
                label={getMicrocopy(MICROCOPY.CHECKOUT_BUYER_INFORMATION_INPUT_LICENCE_NUMBER_LABEL)}
                type="text"
                note={
                  <p className={styles.licenceHelperWrapper}>
                    <span className={styles.licenseText}>
                      {getMicrocopy(MICROCOPY.CHECKOUT_BUYER_INFORMATION_INPUT_LICENCE_NUMBER_HELPER)}
                    </span>
                    <button type="button" className={styles.licenseFinderButton} onClick={onOpenLicenseFinder}>
                      <span className={styles.egText}>
                        {getMicrocopy(MICROCOPY.CHECKOUT_BUYER_INFORMATION_BUSINESS_FORM_LICENCE_NUMBER_SEE_EXAMPLE)}
                      </span>
                    </button>
                  </p>
                }
              />
              <footer className={styles.footer}>
                <Button type="submit" isLoading={props.isSubmitting} fullWidth data-testid="submit">
                  {getMicrocopy(MICROCOPY.PERSONALIZED_QUOTE_PERSONAL_DETAILS_FORM_CTA)}
                </Button>
              </footer>
              <LicenseFinder {...licenseData} open={isLicenseFinderOpen} onClose={onCloseLicenseFinder} />
            </Form>
          )}
        </Formik>
      </div>
    </>
  );
};
